import { useCallback, useEffect, useState } from "react";
import PouchDB from "pouchdb";
import { DatabaseIdentifier } from "./manager";
import { useCurrentDatabaseManager, useDatabases } from "./useDatabases";
import { useStableValue } from "../useStableValue";
import _ from "lodash";
import { filterUndefined } from "../../utils/utils";
import { PouchListReturn } from "./usePouchAllDocs";
import { PouchOptions } from "./common";

export function usePouchQuery<T, Result = T>(
  dependencies: DatabaseIdentifier[],
  database: PouchDB.Database<T> | undefined,
  query: string,
  options?: PouchDB.Query.Options<T, Result> & PouchOptions
): PouchListReturn<Result> {
  const [data, setData] = useState<PouchDB.Core.ExistingDocument<Result & PouchDB.Core.AllDocsMeta>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  const mngr = useCurrentDatabaseManager();

  const options_ = useStableValue(options);
  const load = useCallback(async () => {
    if (options_?.enabled === false) {
      return;
    }
    if (database) {
      setLoading(true);
      setError(null);
      try {
        console.log("[POUCH QUERY]", database.name, query);
        const resp = await database.query(query, options_);
        const newData = filterUndefined(resp.rows.map((r) => r.doc));
        setData((d) => (_.isEqual(d, newData) ? d : newData));
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    } else {
      setData(null);
      setLoading(false);
      setError(null);
    }
  }, [database, options_, query]);

  useEffect(() => {
    load().then(() => {});
  }, [load]);
  useEffect(() => {
    mngr?.registerQuery(dependencies, load);
    return () => mngr?.unregisterQuery(dependencies, load);
  }, [mngr, load, dependencies]);

  return { data, loading, error };
}

export function useListProjects(opts?: PouchOptions) {
  const { projects } = useDatabases();
  return usePouchQuery(["projects"], projects, "userprojects/all", { include_docs: true, ...opts });
}

export function useCurrentSettings() {
  const { settings } = useDatabases();
  const { data, error, loading } = usePouchQuery(["settings"], settings, "mapconfigs/all", { include_docs: true });
  if (data) {
    return { data: data[0], error, loading };
  } else {
    return { data, error, loading };
  }
}
